/**
 * Labels flotantes por defecto
 * -----------------------------------------------------------------------------
 * MDB solo flota el label si el markup es exactamente el suyo:
 *
 *   - Inputs/textarea: un wrapper `.form-outline` con el campo y, DESPUÉS, el
 *     `<label class="form-label">` dentro del wrapper.
 *   - Selects: un `<label class="form-label select-label">` como hermano
 *     INMEDIATAMENTE SIGUIENTE del `<select>`.
 *
 * Escribir eso a mano en cada formulario es tedioso y se olvida. Este módulo
 * recorre el DOM y reescribe el markup estándar de Bootstrap
 *
 *   <label for="x">Nombre</label>
 *   <input id="x" class="form-control">
 *
 * a la forma que MDB necesita, antes de que corra `initMDB()`. El resultado es
 * que cualquier campo con label asociado flota solo.
 *
 * Opt-out: `data-mdb-no-float` en el campo o en cualquier ancestro.
 */

// Tipos que no tienen label flotante posible (o que MDB trata aparte).
const SKIP_TYPES = new Set([
  'checkbox',
  'radio',
  'range',
  'file',
  'color',
  'hidden',
  'submit',
  'reset',
  'button',
  'image',
]);

// Contextos donde no hay que tocar nada: ya migrados, markup nativo de
// Bootstrap (`.form-floating`), grupos de input o selects ya inicializados.
const SKIP_CONTEXT = [
  '.form-outline',
  '.form-floating',
  '.input-group',
  '.select-wrapper',
  '.form-check',
  '[data-mdb-no-float]',
].join(', ');

let uid = 0;

function nextId(prefix) {
  uid += 1;
  return `${prefix}-${uid}`;
}

function shouldSkip(field) {
  if (field.closest(SKIP_CONTEXT)) return true;
  if (field.tagName === 'INPUT' && SKIP_TYPES.has((field.type || 'text').toLowerCase())) return true;
  return false;
}

/**
 * Busca el label asociado a un campo: primero por `for`, luego por hermandad.
 * Devuelve null si el campo no tiene label — en ese caso no hay nada que flotar
 * y se deja el markup intacto.
 */
function findLabel(field) {
  if (field.id) {
    const byFor = field.ownerDocument.querySelector(`label[for="${CSS.escape(field.id)}"]`);
    if (byFor) return byFor;
  }

  const prev = field.previousElementSibling;
  if (prev && prev.tagName === 'LABEL' && !prev.htmlFor) return prev;

  const next = field.nextElementSibling;
  if (next && next.tagName === 'LABEL' && !next.htmlFor) return next;

  return null;
}

function linkLabel(field, label, prefix) {
  if (!field.id) field.id = nextId(prefix);
  if (!label.htmlFor) label.htmlFor = field.id;
}

/**
 * input/textarea -> envuelve en `.form-outline` con el label dentro y detrás.
 */
function upgradeInput(field) {
  const label = findLabel(field);
  if (!label) return;

  linkLabel(field, label, 'ct-input');

  const wrapper = field.ownerDocument.createElement('div');
  wrapper.className = 'form-outline';
  wrapper.setAttribute('data-mdb-input-init', '');

  field.parentNode.insertBefore(wrapper, field);
  wrapper.appendChild(field);
  wrapper.appendChild(label);

  label.classList.add('form-label');
  field.classList.add('form-control');
}

/**
 * select -> mueve el label a hermano siguiente y lo marca como `.select-label`.
 */
function upgradeSelect(field) {
  const label = findLabel(field);

  // `.form-select` dibuja la flecha nativa de Bootstrap; MDB monta su propio
  // desplegable sobre `.select`, así que la nativa sobra.
  field.classList.remove('form-select');
  field.classList.add('select');
  field.setAttribute('data-mdb-select-init', '');

  if (!label) return;

  linkLabel(field, label, 'ct-select');
  label.classList.add('form-label', 'select-label');

  // Tiene que ser el hermano inmediatamente posterior: MDB lo busca con
  // SelectorEngine.next(select, '.select-label').
  field.parentNode.insertBefore(label, field.nextSibling);
}

/**
 * Normaliza el markup de formularios de `root` para que MDB flote los labels.
 * Es idempotente: los campos ya migrados quedan dentro de `.form-outline` o
 * `.select-wrapper` y se descartan en la siguiente pasada.
 *
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 */
export function upgradeFloatingLabels(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('select').forEach((field) => {
    if (!shouldSkip(field)) upgradeSelect(field);
  });

  root.querySelectorAll('input, textarea').forEach((field) => {
    if (!shouldSkip(field)) upgradeInput(field);
  });
}

export default upgradeFloatingLabels;

/**
 * Sube el label de un campo al que se le acaba de escribir un valor POR CÓDIGO.
 *
 * MDB decide si flotar el label con la clase `active` en el input, y la pone al
 * construirse o al recibir `focus`/`input`. Cuando el theme escribe un valor
 * directamente —formatear una moneda, poner un número de tarjeta, cargar un
 * registro para editar— no ocurre ninguna de las dos cosas, así que el label se
 * queda encima del valor. Es el mismo fallo que con el autorrelleno, solo que
 * provocado por nosotros.
 *
 * Solo AÑADE la clase: quitarla es cosa de MDB, que lo hace al salir del campo
 * si quedó vacío. Si la quitáramos aquí, un campo enfocado y vacío perdería el
 * label levantado a mitad de escritura.
 *
 * @param {HTMLInputElement|HTMLTextAreaElement} input
 */
export function syncLabel(input) {
  if (!input || !input.value) return;
  input.classList.add('active');
}

/** Tipos que el navegador pinta con su propio formato dentro del campo. */
const NATIVE_DATE_TYPES = ['date', 'time', 'datetime-local', 'month', 'week'];

/**
 * Deja el label arriba, fijo, en los campos de fecha y hora NATIVOS.
 *
 * Un `<input type="date">` nunca está visualmente vacío: Chrome siempre pinta
 * «dd/mm/aaaa» dentro. MDB lo disimula poniéndole `opacity: 0` mientras no esté
 * enfocado y sin valor, y `opacity: 1` en cuanto se enfoca.
 *
 * Ese truco se rompe justo al abrir el calendario del navegador: **el foco se
 * va al desplegable nativo**, así que el label baja —ya no hay `:focus`— pero
 * la opacidad se queda en 1, porque solo se recalcula al enfocar. Resultado: el
 * label cae encima del «dd/mm/aaaa» y se leen los dos textos cruzados. No hay
 * evento al que engancharse: la apertura del calendario nativo no se notifica.
 *
 * La salida es dejar de pelear con la opacidad. El label se queda arriba
 * siempre y el formato se ve debajo, que además le dice al usuario cómo se
 * escribe la fecha. Es lo que hacen la mayoría de sistemas de diseño con estos
 * campos, y por este mismo motivo.
 *
 * NO se aplica a los que el theme convierte en pickers de MDB: esos llevan un
 * input de texto normal y su propio panel.
 *
 * @param {ParentNode} [root=document]
 */
export function pinNativeDateLabels(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('.form-outline input').forEach((input) => {
    if (NATIVE_DATE_TYPES.indexOf(input.getAttribute('type')) === -1) return;

    // Los pickers de MDB se gestionan solos.
    const wrapper = input.closest('.form-outline');
    if (
      wrapper.hasAttribute('data-mdb-datepicker-init') ||
      wrapper.hasAttribute('data-mdb-timepicker-init') ||
      wrapper.hasAttribute('data-mdb-datetimepicker-init')
    ) {
      return;
    }

    if (wrapper.dataset.companyNativeDate) return;

    input.classList.add('active');
    wrapper.classList.add('company-native-date');
    wrapper.dataset.companyNativeDate = 'true';

    // MDB quita `active` en el `blur` cuando el campo está vacío:
    //
    //   _deactivate(event) { if (input.value === '') removeClass(input, 'active'); … }
    //
    // Sin esto el label volvería a bajar en cuanto el foco saliera del campo,
    // que es justo lo que pasa al abrir el calendario del navegador. Se vuelve
    // a poner después, ya con MDB habiendo hecho lo suyo.
    input.addEventListener('blur', () => {
      window.setTimeout(() => input.classList.add('active'), 0);
    });
  });
}
